import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAmazonProductImageCandidates } from '../../utils/productImage';
import { resolveProductImage } from '../../api/products';

function Placeholder({ className, label = 'SKU' }) {
  return (
    <span
      className={`inline-grid shrink-0 place-items-center rounded-md border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] text-[10px] font-semibold uppercase text-[rgb(var(--crm-text-muted))] ${className}`}
      aria-hidden="true"
    >
      {label}
    </span>
  );
}

function isPlaceholderImage(image) {
  return image.naturalWidth <= 1 || image.naturalHeight <= 1;
}

export function ProductThumb({ item, marketplace, className = 'h-12 w-12', token = null }) {
  const asin = item?.asinIn || item?.asinCom;
  const mp = marketplace || item?.order?.marketplace || (item?.asinIn ? 'IN' : 'US');

  const candidates = useMemo(
    () => getAmazonProductImageCandidates(asin, mp, item?.imageUrl || null),
    [asin, mp, item?.imageUrl]
  );

  const [candidateIndex, setCandidateIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setCandidateIndex(0);
    setFailed(false);
    setResolvedUrl(null);
    setResolving(false);
  }, [asin, mp, item?.imageUrl]);

  useEffect(() => {
    if (!failed || !token || !asin || resolving || resolvedUrl) {
      return;
    }

    let cancelled = false;
    setResolving(true);

    resolveProductImage(token, asin, mp)
      .then((payload) => {
        if (!cancelled && payload?.imageUrl) {
          setResolvedUrl(payload.imageUrl);
          setFailed(false);
          setCandidateIndex(0);
        }
      })
      .catch(() => {
        // Keep placeholder when amazon.in/dp scrape also fails.
      })
      .finally(() => {
        if (!cancelled) {
          setResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [failed, token, asin, mp, resolving, resolvedUrl]);

  // Keep body scroll locked while preview is open; Escape closes.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const activeCandidates = resolvedUrl ? [resolvedUrl, ...candidates] : candidates;

  if (!activeCandidates.length || (failed && !resolvedUrl)) {
    return <Placeholder className={className} label={asin ? asin.slice(-4) : 'SKU'} />;
  }

  const imageUrl = activeCandidates[Math.min(candidateIndex, activeCandidates.length - 1)];

  const tryNextCandidate = () => {
    if (candidateIndex < activeCandidates.length - 1) {
      setCandidateIndex((current) => current + 1);
      return;
    }

    setFailed(true);
  };

  const lightbox =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Product image preview"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          >
            <div
              className="relative flex max-h-[90vh] max-w-2xl items-center justify-center rounded-xl bg-white p-2 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imageUrl}
                alt={item?.title || 'Product'}
                className="h-[min(450px,80vh)] w-[min(450px,90vw)] max-w-full rounded-lg object-contain"
              />
              <button
                type="button"
                aria-label="Close image preview"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-xl font-bold text-white transition hover:bg-black/85"
              >
                &times;
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <img
        key={imageUrl}
        src={imageUrl}
        alt={item?.title || 'Product'}
        className={`shrink-0 rounded-md border border-[rgb(var(--crm-border))] bg-white object-cover transition duration-150 hover:border-slate-400 hover:shadow-sm cursor-zoom-in ${className}`}
        loading="lazy"
        onLoad={(event) => {
          if (isPlaceholderImage(event.currentTarget)) {
            tryNextCandidate();
          }
        }}
        onError={tryNextCandidate}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      />
      {lightbox}
    </>
  );
}
